<script lang="ts">
	import { searchTrails } from '$lib/api';
	import '../app.css';
	import { page } from '$app/stores';
	import { onMount, onDestroy } from 'svelte';
	import { browser } from '$app/environment';
	import { goto } from '$app/navigation';
	import { fade, fly, slide } from 'svelte/transition';
	import {
		Menu,
		X as Close,
		Laptop,
		HelpCircle,
		Download,
		Key,
		LogOut,
		LogIn,
		UserPlus,
		Check,
		Copy,
		Users,
		Settings as SettingsIcon,
		ChevronDown,
		User,
		Map,
		Search
	} from 'lucide-svelte';

	let user: { id: string; email: string } | null = null;
	let isLoading = true;
	let showTokenModal = false;
	let showInstallModal = false;
	let copyStatus = { token: false, regUrl: '' };
	let isMobileMenuOpen = false;
	let userMenuOpen = false;
	let searchQuery = '';
	let previewResults: Trail[] = [];
	let isPreviewLoading = false;
	let isSearchFocused = false;
	let showSearchDropdown = false;
	let debounceTimer: ReturnType<typeof setTimeout>;

	// Define Trail type locally if not already globally available or imported
	type Trail = {
		id: string;
		name: string;
		description: string;
		organization: { tag: string };
		// Add other fields if needed for preview
	};

	onMount(() => {
		// if searchQuery param is present, set searchQuery to the param value
		if ($page.url.searchParams.get('q')) {
			searchQuery = $page.url.searchParams.get('q') || '';
		}
	});

	// Simple debounce function
	function debounce(func: () => void, delay: number) {
		clearTimeout(debounceTimer);
		debounceTimer = setTimeout(func, delay);
	}

	// Fetch preview results
	async function fetchPreviewResults() {
		const query = searchQuery.trim();
		if (!query) {
			previewResults = [];
			showSearchDropdown = false;
			return;
		}

		isPreviewLoading = true;
		showSearchDropdown = true; // Show dropdown when searching

		try {
			const response = await searchTrails({ query: query, limit: 5, sort: 'relevance' });
			previewResults = response?.results || [];
		} catch (error) {
			console.error('Error fetching search preview:', error);
			previewResults = []; // Clear results on error
		} finally {
			isPreviewLoading = false;
		}
	}

	// Handle search input changes
	function handleSearchInput() {
		debounce(fetchPreviewResults, 300); // Debounce API calls
	}

	// Clicking outside the menu should close it
	function handleClickOutside(event: MouseEvent) {
		if (userMenuOpen) {
			const target = event.target as HTMLElement;
			const userMenu = document.getElementById('user-menu');
			const userMenuButton = document.getElementById('user-menu-button');

			if (
				userMenu &&
				!userMenu.contains(target) &&
				userMenuButton &&
				!userMenuButton.contains(target)
			) {
				userMenuOpen = false;
			}
		}

		// Close search dropdown if clicking outside
		if (showSearchDropdown) {
			const target = event.target as HTMLElement;
			const searchInput = document.getElementById('navbar-search-input');
			const searchDropdown = document.getElementById('search-dropdown');

			if (
				searchInput &&
				!searchInput.contains(target) &&
				searchDropdown &&
				!searchDropdown.contains(target)
			) {
				showSearchDropdown = false;
			}
		}
	}

	if (browser) {
		document.addEventListener('click', handleClickOutside);
	}

	onDestroy(() => {
		if (browser) {
			document.removeEventListener('click', handleClickOutside);
			clearTimeout(debounceTimer); // Clear timer on component destroy
		}
	});

	// Handle search submission
	function handleSearchSubmit() {
		const trimmedQuery = searchQuery.trim();
		showSearchDropdown = false; // Hide dropdown on submit
		// Force a full navigation with dynamic params for reliable results loading
		if (trimmedQuery) {
			window.location.href = `/trails/search?q=${encodeURIComponent(trimmedQuery)}`;
		} else {
			window.location.href = '/trails';
		}
	}

	// Check if we're on an authentication page (login, register, forgot password, reset password)
	$: isAuthPage =
		$page.url.pathname === '/login' ||
		$page.url.pathname === '/login/' ||
		$page.url.pathname === '/register' ||
		$page.url.pathname === '/register/' ||
		$page.url.pathname === '/forgot-password' ||
		$page.url.pathname === '/forgot-password/' ||
		$page.url.pathname === '/reset-password' ||
		$page.url.pathname === '/reset-password/';

	async function checkAuth() {
		if (!browser) return;

		const token = localStorage.getItem('token');
		if (!token) {
			isLoading = false;
			return;
		}

		try {
			const response = await fetch('/api/auth/me', {
				headers: {
					Authorization: `Bearer ${token}`
				}
			});
			if (response.ok) {
				const data = await response.json();
				user = {
					id: data.id,
					email: data.email
				};
			} else {
				localStorage.removeItem('token');
				if (
					!['/login', '/register'].includes($page.url.pathname) &&
					!$page.url.pathname.startsWith('/docs')
				) {
					goto('/login');
				}
			}
		} catch (error) {
			console.error('Failed to fetch user:', error);
			localStorage.removeItem('token');
			if (
				!['/login', '/register'].includes($page.url.pathname) &&
				!$page.url.pathname.startsWith('/docs')
			) {
				goto('/login');
			}
		}
		isLoading = false;
	}

	onMount(checkAuth);

	function handleLogout() {
		if (browser) {
			localStorage.removeItem('token');
			user = null;
			goto('/login');
		}
	}

	function showToken() {
		showTokenModal = true;
	}

	function closeTokenModal() {
		showTokenModal = false;
		copyStatus.token = false;
	}

	async function copyToClipboard(text: string, type: 'token' | 'regUrl') {
		await navigator.clipboard.writeText(text);

		if (type === 'token') {
			copyStatus.token = true;
			setTimeout(() => {
				copyStatus.token = false;
			}, 2000);
		} else {
			copyStatus.regUrl = text;
			setTimeout(() => {
				copyStatus.regUrl = '';
			}, 2000);
		}
	}

	function showInstallInstructions() {
		showInstallModal = true;
	}

	function closeInstallModal() {
		showInstallModal = false;
	}
</script>

<svelte:head>
	<script
		defer
		src="https://static.cloudflareinsights.com/beacon.min.js"
		data-cf-beacon={`{"token": "9d19d44fcd544484bccf5b9397fefd85"}`}
	></script>

	<script
		defer
		src="https://cloud.umami.is/script.js"
		data-website-id="4a0a9553-b43b-4b98-8478-4ebae1225e89"
	></script>

	<script
		src="https://analytics.ahrefs.com/analytics.js"
		data-key="F606cQY7zmMHD1XEACSGLA"
		async
	></script>
</svelte:head>

{#if isLoading && browser}
	<div class="min-h-screen flex items-center justify-center bg-surface-0">
		<div class="animate-spin h-10 w-10 text-primary-500">
			<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
				<circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
				<path
					class="opacity-75"
					fill="currentColor"
					d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
				/>
			</svg>
		</div>
	</div>
{:else}
	<div class="flex flex-col min-h-screen bg-surface-0">
		{#if !isAuthPage}
			<header class="bg-surface-1 shadow-md relative z-50 sticky top-0">
				<div class="px-4 sm:px-6 lg:px-8">
					<div class="flex justify-between h-16">
						<div class="flex">
							<div class="flex-shrink-0 flex items-center mr-8">
								<a href="/" class="text-xl font-bold text-white">
									<div class="flex items-center">
										<img src="/logo.png" alt="Herd - Three cute bulls" class="h-12 w-auto mr-2" />
										<span>Herd</span>
									</div>
								</a>
							</div>
							<!-- Mobile menu button -->
							<div class="flex items-center md:hidden">
								<button
									type="button"
									class="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-white hover:bg-surface-2 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
									aria-controls="mobile-menu"
									aria-expanded="false"
									on:click={() => (isMobileMenuOpen = !isMobileMenuOpen)}
								>
									<span class="sr-only">Open main menu</span>
									{#if !isMobileMenuOpen}
										<Menu class="block h-6 w-6" />
									{:else}
										<Close class="block h-6 w-6" />
									{/if}
								</button>
							</div>
							<!-- Desktop navigation -->
							<div class="hidden md:flex md:space-x-8 flex-grow">
								<!-- Search Input -->
								<form
									on:submit|preventDefault={handleSearchSubmit}
									class="relative ml-4 flex-grow max-w-xl lg:max-w-2xl flex items-center"
								>
									<input
										id="navbar-search-input"
										type="text"
										bind:value={searchQuery}
										class="input w-full md:w-64 lg:w-96 !py-2.5 !pl-10 text-sm bg-surface-2/70 border-surface-3 hover:bg-surface-2 focus:bg-surface-2 focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500/70 transition-all duration-200 rounded-md"
										on:input={handleSearchInput}
										on:focus={() => {
											isSearchFocused = true;
											if (searchQuery.trim()) showSearchDropdown = true;
										}}
										on:blur={() => {
											// Small delay to allow click events on dropdown to register
											setTimeout(() => {
												isSearchFocused = false;
											}, 100);
										}}
									/>
									<!-- Custom placeholder with "view all" link -->
									{#if !searchQuery}
										<div
											class="absolute top-0 left-0 right-0 bottom-0 pointer-events-none flex items-center pl-10 pr-3"
										>
											<span class="text-gray-400 text-sm">
												Search trails or
												<button
													class="text-primary-400 cursor-pointer pointer-events-auto"
													on:click|stopPropagation={(e) => {
														e.preventDefault();
														window.location.href = '/trails';
													}}
												>
													open Trail Hub
												</button>
											</span>
										</div>
									{/if}
									<div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
										<Search class="h-5 w-5 text-primary-400" />
									</div>

									<!-- Clear icon button -->
									{#if searchQuery}
										<div class="absolute inset-y-0 right-0 pr-3 flex items-center">
											<button
												type="button"
												class="text-gray-400 hover:text-white focus:outline-none"
												on:click={() => {
													searchQuery = '';
													showSearchDropdown = false;
												}}
											>
												<Close class="h-4 w-4" />
											</button>
										</div>
									{/if}

									<!-- Search Dropdown -->
									{#if showSearchDropdown}
										<div
											id="search-dropdown"
											class="absolute top-full left-0 right-0 mt-1.5 bg-surface-2 border border-surface-3 rounded-md shadow-lg overflow-hidden z-10"
											transition:fade={{ duration: 150 }}
										>
											{#if isPreviewLoading}
												<div class="p-4 text-center text-gray-400 text-sm">Loading...</div>
											{:else if previewResults.length > 0}
												<ul class="max-h-80 overflow-y-auto">
													{#each previewResults as trail (trail.id)}
														<li>
															<a
																href={`/trails/@${trail.organization.tag}/${
																	trail.name.split('/')[1]
																}`}
																class="block px-4 py-2.5 hover:bg-surface-3 text-sm transition-colors duration-100"
																on:click={() => (showSearchDropdown = false)}
															>
																<p class="font-medium text-white truncate">{trail.name}</p>
																<p class="text-xs text-gray-400 truncate mt-0.5">
																	{trail.description || 'No description'}
																</p>
															</a>
														</li>
													{/each}
												</ul>
											{:else if !isPreviewLoading && previewResults.length === 0 && searchQuery.trim()}
												<div class="p-4 text-center text-gray-400 text-sm">No results found.</div>
											{/if}
											<div class="border-t border-surface-3">
												<button
													class="block w-full text-center px-4 py-2.5 text-sm font-medium text-primary-400 hover:bg-surface-3 transition-colors duration-100"
													on:click={() => {
														showSearchDropdown = false;
														window.location.href = `/trails/search?q=${encodeURIComponent(
															searchQuery.trim()
														)}`;
													}}
												>
													View all results for "{searchQuery.trim()}"
												</button>
											</div>
										</div>
									{/if}
								</form>
								<div class="hidden lg:flex lg:items-center lg:space-x-8 ml-auto">
									<a
										href="/docs"
										class="nav-link {$page.url.pathname.startsWith('/docs')
											? 'nav-link-active'
											: 'nav-link-inactive'}"
									>
										<HelpCircle class="w-5 h-5 mr-1.5" />
										Docs
									</a>
								</div>
							</div>
						</div>
						<div class="flex items-center">
							{#if user}
								<div class="hidden sm:flex items-center space-x-4">
									<button on:click={showToken} class="btn-accent gap-2 py-2 rounded-full text-xs">
										<Key class="w-4 h-4" />
										<span>Copy Token</span>
									</button>
									<button
										on:click={showInstallInstructions}
										class="btn-secondary text-sm gap-2 py-2 rounded-full text-xs"
									>
										<Download class="w-4 h-4" />
										<span>Get Herd</span>
									</button>
								</div>
								<div class="hidden sm:flex sm:items-center">
									<div class="relative ml-3">
										<!-- User menu dropdown button -->
										<button
											id="user-menu-button"
											type="button"
											class="flex items-center space-x-2 rounded-full bg-surface-2 hover:bg-surface-3 p-1.5 focus:outline-none focus:ring-2 focus:ring-primary-500"
											on:click={() => (userMenuOpen = !userMenuOpen)}
										>
											<div
												class="w-7 h-7 rounded-full bg-primary-400 text-white flex items-center justify-center"
											>
												{user.email.charAt(0).toUpperCase()}
											</div>
											<ChevronDown class="w-4 h-4 text-gray-300" />
										</button>

										<!-- User menu dropdown -->
										{#if userMenuOpen}
											<button
												id="user-menu"
												class="absolute right-0 mt-2 w-48 origin-top-right bg-surface-2 border border-surface-3 rounded-md shadow-lg py-1"
												transition:fade={{ duration: 150 }}
												on:click={() => (userMenuOpen = false)}
											>
												<div class="px-4 py-2 border-b border-surface-3 flex flex-col items-start">
													<p class="text-xs text-gray-400">Signed in as</p>
													<p class="text-sm font-medium text-gray-200 truncate pr-2">
														{user.email}
													</p>
												</div>
												<a
													href="/devices"
													class="block px-4 py-2 text-sm text-gray-200 hover:bg-surface-3 flex items-center"
												>
													<Laptop class="w-4 h-4 mr-2 text-gray-400" />
													Devices
												</a>
												<a
													href="/settings/profile"
													class="block px-4 py-2 text-sm text-gray-200 hover:bg-surface-3 flex items-center"
												>
													<User class="w-4 h-4 mr-2 text-gray-400" />
													Profile
												</a>
												<a
													href="/settings/organizations"
													class="block px-4 py-2 text-sm text-gray-200 hover:bg-surface-3 flex items-center"
												>
													<Users class="w-4 h-4 mr-2 text-gray-400" />
													Organizations
												</a>
												<a
													href="/settings/profile"
													class="block px-4 py-2 text-sm text-gray-200 hover:bg-surface-3 flex items-center"
												>
													<SettingsIcon class="w-4 h-4 mr-2 text-gray-400" />
													Settings
												</a>
												<div class="border-t border-surface-3 my-1" />
												<button
													on:click={handleLogout}
													class="w-full text-left block px-4 py-2 text-sm text-gray-200 hover:bg-surface-3 flex items-center"
												>
													<LogOut class="w-4 h-4 mr-2 text-gray-400" />
													Log out
												</button>
											</button>
										{/if}
									</div>
								</div>
							{:else if browser && !['/login', '/register'].includes($page.url.pathname) && !$page.url.pathname.startsWith('/docs')}
								<div class="flex items-center space-x-4">
									<a href="/login" class="btn-secondary text-sm gap-2">
										<LogIn class="w-4 h-4" />
										<span class="hidden md:block">Log in</span>
									</a>
									<a href="/register" class="btn-primary text-sm gap-2">
										<UserPlus class="w-4 h-4" />
										<span class="hidden md:block">Sign up</span>
									</a>
								</div>
							{:else if browser && !['/login', '/register'].includes($page.url.pathname) && $page.url.pathname.startsWith('/docs')}
								<div class="flex items-center space-x-4">
									<a href="/login" class="btn-secondary text-sm">
										<LogIn class="w-4 h-4 mr-1.5" />
										<span class="hidden md:block">Log in</span>
									</a>
									<a href="/register" class="btn-primary text-sm">
										<UserPlus class="w-4 h-4 mr-1.5" />
										<span class="hidden md:block">Sign up</span>
									</a>
								</div>
							{/if}
						</div>
					</div>
				</div>
			</header>
		{/if}

		<!-- Mobile menu -->
		{#if isMobileMenuOpen}
			<div class="md:hidden bg-surface-1 shadow-lg" id="mobile-menu" transition:slide>
				<!-- Mobile Search -->
				<div class="px-4 pt-3 pb-2">
					<form on:submit|preventDefault={handleSearchSubmit} class="relative">
						<input
							type="text"
							bind:value={searchQuery}
							class="input w-full !py-1.5 !pl-9 text-sm bg-surface-2 border-surface-3 focus:ring-primary-500 focus:border-primary-500"
						/>
						<!-- Custom placeholder with "view all" link for mobile -->
						{#if !searchQuery}
							<div
								class="absolute top-0 left-0 right-0 bottom-0 pointer-events-none flex items-center pl-9 pr-3"
							>
								<span class="text-gray-400 text-sm">
									Search trails or
									<span
										class="text-primary-400 cursor-pointer pointer-events-auto"
										on:click|stopPropagation={(e) => {
											e.preventDefault();
											window.location.href = '/trails';
											isMobileMenuOpen = false;
										}}
									>
										open Trail Hub
									</span>
								</span>
							</div>
						{/if}
						<div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
							<Search class="h-4 w-4 text-gray-400" />
						</div>

						<!-- Clear icon button for mobile -->
						{#if searchQuery}
							<div class="absolute inset-y-0 right-0 pr-3 flex items-center">
								<button
									type="button"
									class="text-gray-400 hover:text-white focus:outline-none"
									on:click={() => {
										searchQuery = '';
									}}
								>
									<Close class="h-4 w-4" />
								</button>
							</div>
						{/if}
					</form>
				</div>

				<div class="px-2 pt-2 pb-3 space-y-1">
					{#if user}
						<a
							href="/devices"
							class="block px-3 py-2 rounded-md text-base font-medium {$page.url.pathname ===
							'/devices'
								? 'bg-primary-500 text-white'
								: 'text-gray-300 hover:bg-surface-2 hover:text-white'}"
						>
							<div class="flex items-center">
								<Laptop class="w-5 h-5 mr-2" />
								Devices
							</div>
						</a>

						<button
							on:click={showToken}
							class="w-full text-left block px-3 py-2 rounded-md text-base font-medium text-gray-300 hover:bg-surface-2 hover:text-white"
						>
							<div class="flex items-center">
								<Key class="w-5 h-5 mr-2" />
								Copy Token
							</div>
						</button>

						<button
							on:click={showInstallInstructions}
							class="w-full text-left block px-3 py-2 rounded-md text-base font-medium text-gray-300 hover:bg-surface-2 hover:text-white"
						>
							<div class="flex items-center">
								<Download class="w-5 h-5 mr-2" />
								Install Herd
							</div>
						</button>

						<a
							href="/settings/profile"
							class="block px-3 py-2 rounded-md text-base font-medium {$page.url.pathname.startsWith(
								'/settings'
							)
								? 'bg-primary-500 text-white'
								: 'text-gray-300 hover:bg-surface-2 hover:text-white'}"
						>
							<div class="flex items-center">
								<svg
									xmlns="http://www.w3.org/2000/svg"
									viewBox="0 0 24 24"
									fill="none"
									stroke="currentColor"
									stroke-width="2"
									stroke-linecap="round"
									stroke-linejoin="round"
									class="w-5 h-5 mr-2"
								>
									<path
										d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"
									/>
									<circle cx="12" cy="12" r="3" />
								</svg>
								Settings
							</div>
						</a>

						<a
							href="/organizations"
							class="block px-3 py-2 rounded-md text-base font-medium {$page.url.pathname.startsWith(
								'/organizations'
							)
								? 'bg-primary-500 text-white'
								: 'text-gray-300 hover:bg-surface-2 hover:text-white'}"
						>
							<div class="flex items-center">
								<Users class="w-5 h-5 mr-2" />
								Organizations
							</div>
						</a>

						<button
							on:click={handleLogout}
							class="w-full text-left block px-3 py-2 rounded-md text-base font-medium text-gray-300 hover:bg-surface-2 hover:text-white"
						>
							<div class="flex items-center">
								<LogOut class="w-5 h-5 mr-2" />
								Log out
							</div>
						</button>
					{/if}
					<a
						href="/trails"
						class="block px-3 py-2 rounded-md text-base font-medium {$page.url.pathname.startsWith(
							'/trails'
						)
							? 'bg-primary-500 text-white'
							: 'text-gray-300 hover:bg-surface-2 hover:text-white'}"
					>
						<div class="flex items-center">
							<Map class="w-5 h-5 mr-2" />
							Trails
						</div>
					</a>
					<a
						href="/docs"
						class="block px-3 py-2 rounded-md text-base font-medium {$page.url.pathname.startsWith(
							'/docs'
						)
							? 'bg-primary-500 text-white'
							: 'text-gray-300 hover:bg-surface-2 hover:text-white'}"
					>
						<div class="flex items-center">
							<HelpCircle class="w-5 h-5 mr-2" />
							Docs
						</div>
					</a>
				</div>
			</div>
		{/if}

		<main class="flex-grow flex">
			<div class="flex-grow w-full">
				<div
					class:max-w-7xl={!$page.url.pathname.startsWith('/trails') &&
						!isAuthPage &&
						!$page.url.pathname.startsWith('/docs')}
					class:mx-auto={!$page.url.pathname.startsWith('/trails') && !isAuthPage}
					class:px-4={!$page.url.pathname.startsWith('/trails') &&
						!isAuthPage &&
						!$page.url.pathname.startsWith('/docs')}
					class:sm:px-6={!$page.url.pathname.startsWith('/trails') &&
						!isAuthPage &&
						!$page.url.pathname.startsWith('/docs')}
					class:lg:px-8={!$page.url.pathname.startsWith('/trails') &&
						!isAuthPage &&
						!$page.url.pathname.startsWith('/docs')}
					class="w-full h-full"
				>
					<slot />
				</div>
			</div>
		</main>

		{#if !isAuthPage}
			<footer class="bg-surface-1 border-t border-surface-2 mt-32">
				<div class="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
					{#if !$page.url.pathname.startsWith('/devices') && !isAuthPage}
						<div class="grid grid-cols-1 md:grid-cols-4 gap-8">
							<!-- Copyright Column - Always visible -->
							<div>
								<div class="flex items-center mb-4 gap-2">
									<img src="/logo.png" alt="Herd - Three cute bulls" class="h-10 w-auto" />
									<h3 class="text-white font-bold">Herd</h3>
								</div>
								<div class="text-sm text-gray-400 mb-2">
									&copy; {new Date().getFullYear()} Monitoro, Omneity Labs.
								</div>
								<div class="text-sm text-gray-400">All rights reserved.</div>
							</div>

							<!-- Docs Column -->
							<div>
								<h3 class="text-white font-medium mb-4">Documentation</h3>
								<ul class="space-y-2">
									<li>
										<a
											href="/docs/getting-started"
											class="text-sm text-gray-400 hover:text-gray-300">Getting Started</a
										>
									</li>
									<li>
										<a
											href="/docs/automation-basics"
											class="text-sm text-gray-400 hover:text-gray-300">Automation Basics</a
										>
									</li>
									<li>
										<a
											href="/docs/data-extraction"
											class="text-sm text-gray-400 hover:text-gray-300">Data Extraction</a
										>
									</li>
									<li>
										<a href="/docs/reference" class="text-sm text-gray-400 hover:text-gray-300"
											>Reference</a
										>
									</li>
									<li>
										<a href="/docs/help" class="text-sm text-gray-400 hover:text-gray-300">Help</a>
									</li>
								</ul>
							</div>

							<!-- Comparisons Column -->
							<div>
								<h3 class="text-white font-medium mb-4">Comparisons</h3>
								<ul class="space-y-2">
									<li>
										<a
											href="/docs/alternative-herd-vs-puppeteer"
											class="text-sm text-gray-400 hover:text-gray-300">Puppeteer vs Herd</a
										>
									</li>
									<li>
										<a
											href="/docs/alternative-herd-vs-selenium"
											class="text-sm text-gray-400 hover:text-gray-300">Selenium vs Herd</a
										>
									</li>
									<li>
										<a
											href="/docs/alternative-herd-vs-apify"
											class="text-sm text-gray-400 hover:text-gray-300">Apify vs Herd</a
										>
									</li>
									<li>
										<a
											href="/docs/alternative-herd-vs-browserbase"
											class="text-sm text-gray-400 hover:text-gray-300">Browserbase vs Herd</a
										>
									</li>
									<li>
										<a
											href="/docs/alternative-herd-vs-firecrawl"
											class="text-sm text-gray-400 hover:text-gray-300">Firecrawl vs Herd</a
										>
									</li>
									<li>
										<a
											href="/docs/alternative-herd-vs-mcp-sdk"
											class="text-sm text-gray-400 hover:text-gray-300">MCP vs Herd</a
										>
									</li>
									<li>
										<a
											href="/docs/alternative-herd-vs-monitoro"
											class="text-sm text-gray-400 hover:text-gray-300">Monitoro vs Herd</a
										>
									</li>
								</ul>
							</div>

							<!-- Legal Column -->
							<div>
								<h3 class="text-white font-medium mb-4">Legal</h3>
								<ul class="space-y-2">
									<li>
										<a href="/terms" class="text-sm text-gray-400 hover:text-gray-300"
											>Terms of Service</a
										>
									</li>
									<li>
										<a href="/privacy" class="text-sm text-gray-400 hover:text-gray-300"
											>Privacy Policy</a
										>
									</li>
									<li>
										<a
											href="/docs/security-privacy"
											class="text-sm text-gray-400 hover:text-gray-300">Security & Privacy</a
										>
									</li>
								</ul>
							</div>
						</div>
					{:else}
						<!-- Only show copyright for app routes -->
						<div class="flex flex-col items-center justify-between md:flex-row">
							<div class="text-sm text-gray-400">
								&copy; {new Date().getFullYear()} Monitoro, Omneity Labs. All rights reserved.
							</div>
							<div class="mt-4 md:mt-0">
								<div class="flex space-x-6 text-gray-500">
									<a href="/terms" class="text-sm hover:text-gray-300">Terms of Service</a>
									<a href="/privacy" class="text-sm hover:text-gray-300">Privacy Policy</a>
								</div>
							</div>
						</div>
					{/if}
				</div>
			</footer>
		{/if}
	</div>
{/if}

{#if showTokenModal}
	<div
		class="fixed z-20 inset-0 overflow-y-auto"
		aria-labelledby="modal-title"
		role="dialog"
		aria-modal="true"
		transition:fade={{ duration: 200 }}
	>
		<div
			class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0"
		>
			<div class="modal-backdrop" aria-hidden="true" on:click={closeTokenModal} />

			<span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true"
				>&#8203;</span
			>

			<div class="modal-container" transition:fly={{ y: 10, duration: 300 }}>
				<div class="absolute top-0 right-0 pt-4 pr-4">
					<button
						type="button"
						class="bg-surface-1 rounded-md text-gray-400 hover:text-gray-200 focus:outline-none"
						on:click={closeTokenModal}
					>
						<span class="sr-only">Close</span>
						<svg
							class="h-6 w-6"
							xmlns="http://www.w3.org/2000/svg"
							fill="none"
							viewBox="0 0 24 24"
							stroke="currentColor"
							aria-hidden="true"
						>
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M6 18L18 6M6 6l12 12"
							/>
						</svg>
					</button>
				</div>

				<div class="sm:flex sm:items-start">
					<div
						class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-primary-400 sm:mx-0 sm:h-10 sm:w-10"
					>
						<Key class="h-6 w-6 text-white" />
					</div>
					<div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
						<h3 class="text-lg leading-6 font-medium text-white" id="modal-title">
							Your Authentication Token
						</h3>
						<div class="mt-4">
							<p class="text-sm text-gray-300 mb-2">
								Use this token to authenticate your API requests:
							</p>
							<div class="mt-2 flex rounded-md shadow-sm">
								<input
									type="text"
									readonly
									value={browser ? localStorage.getItem('token') || '' : ''}
									class="input rounded-r-none"
								/>
								<button
									type="button"
									class="btn-accent rounded-l-none border-l-0"
									on:click={() => copyToClipboard(localStorage.getItem('token') || '', 'token')}
								>
									{#if copyStatus.token}
										<Check class="h-4 w-4 mr-1" />
										Copied!
									{:else}
										<Copy class="h-4 w-4 mr-1" />
										Copy
									{/if}
								</button>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
{/if}

{#if showInstallModal}
	<div
		class="fixed z-10 inset-0 overflow-y-auto"
		aria-labelledby="modal-title"
		role="dialog"
		aria-modal="true"
	>
		<div
			class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0"
		>
			<div
				class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
				aria-hidden="true"
				on:click={closeInstallModal}
			/>

			<span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true"
				>&#8203;</span
			>

			<div
				class="inline-block align-bottom bg-surface-1 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full"
			>
				<div class="absolute top-0 right-0 pt-4 pr-4">
					<button
						type="button"
						class="bg-transparent rounded-md text-gray-400 hover:text-gray-200 focus:outline-none"
						on:click={closeInstallModal}
					>
						<span class="sr-only">Close</span>
						<svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
							<path
								stroke-linecap="round"
								stroke-linejoin="round"
								stroke-width="2"
								d="M6 18L18 6M6 6l12 12"
							/>
						</svg>
					</button>
				</div>

				<div class="px-6 pt-6 pb-8">
					<div class="flex items-center mb-5">
						<div class="flex-shrink-0 bg-primary-500/20 p-2 rounded-full">
							<Download class="h-6 w-6 text-primary-400" />
						</div>
						<h3 class="ml-3 text-xl font-semibold text-white" id="modal-title">
							Install Herd Extension
						</h3>
					</div>

					<div class="mt-2">
						<p class="text-sm text-gray-300 mb-6">
							Follow these steps to get started with Herd in your browser.
						</p>

						<div class="space-y-5">
							<div class="flex items-start">
								<div
									class="flex-shrink-0 h-8 w-8 rounded-full bg-primary-500/20 flex items-center justify-center text-primary-400 font-medium"
								>
									1
								</div>
								<div class="ml-4">
									<p class="text-sm font-medium text-gray-200">Download the extension</p>
									<p class="mt-1 text-xs text-gray-400">
										Get Herd from the <a
											href="/docs/getting-started"
											class="text-primary-400 hover:text-primary-300 underline">Getting Started</a
										> page
									</p>
								</div>
							</div>

							<div class="flex items-start">
								<div
									class="flex-shrink-0 h-8 w-8 rounded-full bg-primary-500/20 flex items-center justify-center text-primary-400 font-medium"
								>
									2
								</div>
								<div class="ml-4">
									<p class="text-sm font-medium text-gray-200">Open Chrome Extensions</p>
									<p class="mt-1 text-xs text-gray-400">
										Navigate to <code class="px-1.5 py-0.5 bg-surface-3 rounded text-gray-300"
											>chrome://extensions</code
										> in your browser
									</p>
								</div>
							</div>

							<div class="flex items-start">
								<div
									class="flex-shrink-0 h-8 w-8 rounded-full bg-primary-500/20 flex items-center justify-center text-primary-400 font-medium"
								>
									3
								</div>
								<div class="ml-4">
									<p class="text-sm font-medium text-gray-200">Enable Developer mode</p>
									<p class="mt-1 text-xs text-gray-400">
										Toggle the switch in the top right corner
									</p>
								</div>
							</div>

							<div class="flex items-start">
								<div
									class="flex-shrink-0 h-8 w-8 rounded-full bg-primary-500/20 flex items-center justify-center text-primary-400 font-medium"
								>
									4
								</div>
								<div class="ml-4">
									<p class="text-sm font-medium text-gray-200">Install the extension</p>
									<p class="mt-1 text-xs text-gray-400">
										Drag and drop <code class="px-1.5 py-0.5 bg-surface-3 rounded text-gray-300"
											>herd-latest.zip</code
										> into the extensions page
									</p>
								</div>
							</div>

							<div class="flex items-start">
								<div
									class="flex-shrink-0 h-8 w-8 rounded-full bg-primary-500/20 flex items-center justify-center text-primary-400 font-medium"
								>
									5
								</div>
								<div class="ml-4">
									<p class="text-sm font-medium text-gray-200">Register your device</p>
									<p class="mt-1 text-xs text-gray-400">
										Click the registration URL from your devices page
									</p>
								</div>
							</div>
						</div>
					</div>

					<div class="mt-8 flex justify-end">
						<a href="/docs/getting-started" class="btn-secondary mr-3 text-sm"> View Docs </a>
						<button type="button" class="btn-primary text-sm" on:click={closeInstallModal}>
							Got it
						</button>
					</div>
				</div>
			</div>
		</div>
	</div>
{/if}

<style>
	.nav-link {
		height: 100%;
	}
</style>
